/**
 * Timeline Event Row — renders a single event in the contact timeline.
 * Maps event types to icons and colors for consistent visual language.
 */

import {
  MessageCircle, Package, CreditCard, ClipboardList,
  Tag, UserPlus, UserPen, Brain, Award, Settings, Send, FileText,
  TrendingUp, CheckCircle, XCircle, Pencil, Phone, Calendar,
  Ban, Trash2, RotateCcw, Merge,
} from 'lucide-react'
import type { TimelineEvent } from '@/types/crm'

interface TimelineEventRowProps {
  event: TimelineEvent
}

// Map event types to icons and default colors
const EVENT_CONFIG: Record<string, { icon: React.ElementType; color: string }> = {
  message_received: { icon: MessageCircle, color: 'text-green-500' },
  message_sent: { icon: Send, color: 'text-blue-500' },
  order_created: { icon: Package, color: 'text-purple-500' },
  order_status_changed: { icon: Package, color: 'text-purple-400' },
  payment_received: { icon: CreditCard, color: 'text-emerald-500' },
  flow_completed: { icon: ClipboardList, color: 'text-indigo-500' },
  tag_added: { icon: Tag, color: 'text-amber-500' },
  tag_removed: { icon: Tag, color: 'text-muted-foreground' },
  group_added: { icon: UserPlus, color: 'text-teal-500' },
  contact_created: { icon: UserPlus, color: 'text-blue-500' },
  contact_updated: { icon: UserPen, color: 'text-blue-400' },
  ai_insight_extracted: { icon: Brain, color: 'text-violet-500' },
  loyalty_enrolled: { icon: Award, color: 'text-yellow-500' },
  note_added: { icon: Pencil, color: 'text-muted-foreground' },
  task_created: { icon: CheckCircle, color: 'text-orange-500' },
  task_completed: { icon: CheckCircle, color: 'text-success' },
  call_logged: { icon: Phone, color: 'text-blue-500' },
  meeting_logged: { icon: Calendar, color: 'text-purple-500' },
  deal_created: { icon: TrendingUp, color: 'text-blue-500' },
  deal_stage_changed: { icon: TrendingUp, color: 'text-blue-400' },
  deal_won: { icon: TrendingUp, color: 'text-success' },
  deal_lost: { icon: XCircle, color: 'text-destructive' },
  system: { icon: Settings, color: 'text-muted-foreground' },
  contact_blocked: { icon: Ban, color: 'text-red-500' },
  contact_unblocked: { icon: CheckCircle, color: 'text-green-500' },
  contact_deleted: { icon: Trash2, color: 'text-red-500' },
  contact_restored: { icon: RotateCcw, color: 'text-green-500' },
  contacts_merged: { icon: Merge, color: 'text-purple-500' },
}

const DEFAULT_CONFIG = { icon: FileText, color: 'text-muted-foreground' }

export function TimelineEventRow({ event }: TimelineEventRowProps) {
  const config = EVENT_CONFIG[event.eventType] || DEFAULT_CONFIG
  const Icon = config.icon
  const color = event.color || config.color

  const time = new Date(event.createdAt).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })

  return (
    <div className="flex gap-3 py-2 px-3 rounded-lg hover:bg-muted transition-colors group">
      {/* Icon */}
      <div className={`mt-0.5 shrink-0 ${color}`}>
        <Icon className="w-4 h-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm text-foreground font-medium leading-snug">
            {event.title}
          </p>
          <span className="text-xs text-muted-foreground shrink-0 mt-0.5">
            {time}
          </span>
        </div>
        {event.description && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {event.description}
          </p>
        )}
        {event.actorName && event.actorType !== 'system' && (
          <p className="text-xs text-muted-foreground mt-0.5">
            by {event.actorName}
          </p>
        )}
      </div>
    </div>
  )
}
